import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { TabRailContext } from "./useTabRailContext";
import { TabRailList } from "./TabRailList";
import { TabRailTabPart } from "./TabRailTab";

/**
 * A molecule composed as a namespaced compound component -- the row of tabs above a Collection's
 * board, plus whatever trailing controls the consumer chooses to place after it. The root only
 * owns selection state (shared context every `TabRail.Tab` needs) and the outer flex row; the
 * trailing controls are just ordinary elements the consumer supplies, so their icons, labels,
 * menus and actions stay the consumer's concern rather than TabRail's.
 *
 * @example
 * ```tsx
 * <TabRail activeTabId={id} onSelectTab={select}>
 *   <TabRail.List>
 *     {tabs.map((tab) => (
 *       <TabRail.Tab key={tab.id} tab={tab} onDelete={() => remove(tab.id)} onRename={(label) => rename(tab.id, label)} />
 *     ))}
 *   </TabRail.List>
 *   <Button variant="ghost" color="muted" size="sm" iconOnly onClick={addTab} hoverLabel={t("New collection")}>
 *     <Plus className="h-3.5 w-3.5" />
 *   </Button>
 * </TabRail>
 * ```
 */
export interface TabRailProps {
  activeTabId: string;
  onSelectTab: (id: string) => void;
  className?: string;
  children: ReactNode;
}

const TabRailRoot = ({ activeTabId, onSelectTab, className, children }: TabRailProps) => (
  <TabRailContext.Provider value={{ activeTabId, onSelectTab }}>
    <div data-slot="tab-rail" className={cn("flex items-center gap-1", className)}>
      {children}
    </div>
  </TabRailContext.Provider>
);

export const TabRail = Object.assign(TabRailRoot, {
  List: TabRailList,
  Tab: TabRailTabPart,
});
